import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { AbcCore, DefinitionDocumentReference, ProfileInfoViewModel } from '@barthauer/abc/core';
import { ProfileInfo, ProfileService, UserService } from '@barthauer/abc/core/authentication';
import { DataService, ReportImageInfo, ReportImageInfoQueryDef } from '@barthauer/abc/core/data';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { BceService, GroupPacket } from '@barthauer/abc/management/configuration-explorer';
import { MessageBoxService, MessageMode, MessageSettings } from '@barthauer/abc/messages';
import { ParameterHelper, ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { AbcContainerService, AbcNotificationComponent, INotification, ToastType } from '@barthauer/abc/ui-components';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { ParameterService } from 'libs/abc/queries/parameters/values-editor/src/lib/services/parameter.service';
import { ProfileInfoWithArenaViewModel } from 'libs/basys/map-frame/src/lib/viewModels/mapsData.viewModel';
import { PDFDocument, PDFEmbeddedPage, PDFPageDrawImageOptions } from 'pdf-lib';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LAYER_PRINT_BOX } from '../../map.component';
import { MapLoader } from '../../map.loader';
import { MapView } from '../../map.view';
import { MapLayer } from '../../models/map.model';
import {
  PrintResolution,
  PrintScale,
  PrintSettingsStorage,
  PrintTemplate,
  PrintTemplateSelector,
} from '../../models/print.model';
import { MapService } from '../../services/map.service';
import { RotateInteraction } from './rotate.interaction';
import { F } from '@angular/cdk/keycodes';

declare var ol: any;

const DEFAULT_SCALE: number = 500;

@Component({
  selector: 'abc-graphics-print',
  templateUrl: './print.component.html',
  styleUrls: ['./print.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PrintComponent implements AfterViewInit, OnInit {
  private unsubscribe$: Subject<void> = new Subject<void>();
  @ViewChild('printDialogContainer', { static: false })
  printDialogContainer: ElementRef;
  @ViewChild('abcNotification', { static: true, read: ViewContainerRef })
  abcNotification: ViewContainerRef;
  @Output() printDialogCloseDemand = new EventEmitter();
  @Output() closePrintDialog = new EventEmitter();

  private mapView: MapView;
  private mapLoader: MapLoader;
  private map: any;
  private selectionLayer: any;
  private selectionLayerSource: any;
  private PRINT_SETTINGS_KEY: string = 'print_settings';
  selectedScale: PrintScale;
  comment: string = '';

  private selectAction = undefined;
  private translate = undefined;
  private rotateInteraction: any = undefined;
  private allowRotation: boolean = undefined;
  private printAreaFeature = undefined;
  private predefinedScales: number[];
  modePS: ProgressSpinnerMode = 'indeterminate';

  private subscriptionCancelled: Subscription;

  hideProgress: boolean = true;
  disabled: boolean = false;
  isRequestingUpdateTemplates = false;
  selectedResolution: PrintResolution;
  printTemplateSelectors: PrintTemplateSelector[];
  private _selectedSelector: PrintTemplateSelector;
  public get selectedSelector(): PrintTemplateSelector {
    return this._selectedSelector;
  }
  public set selectedSelector(selector: PrintTemplateSelector) {
    this._selectedSelector = selector;

    //scales
    let scale: PrintScale = undefined;
    if (this.selectedScale) {
      scale = this._selectedSelector.printTemplate?.validScales?.find((r) => r.value === this.selectedScale.value);
    }
    if (
      !scale &&
      this._selectedSelector.printTemplate?.validScales &&
      this._selectedSelector.printTemplate?.validScales?.length > 0
    ) {
      scale = this._selectedSelector.printTemplate?.getNearestScale(DEFAULT_SCALE);
    }
    this.selectedScale = scale;

    //resolution
    let resolution: PrintResolution = undefined;
    if (this.selectedResolution) {
      resolution = this._selectedSelector.printTemplate?.validResolutions?.find(
        (r) => r.dpi === this.selectedResolution.dpi,
      );
    }
    if (
      !resolution &&
      this._selectedSelector.printTemplate?.validResolutions &&
      this._selectedSelector.printTemplate?.validResolutions?.length > 0
    ) {
      resolution = this._selectedSelector.printTemplate.validResolutions[0];
    }
    this.selectedResolution = resolution;
    this.allowRotation = this._selectedSelector?.printTemplate?.allowRotation;
    this.updateRotateInteraction();
  }
  private get printTemplates(): PrintTemplate[] {
    return this.printTemplateSelectors?.filter((s) => s.printTemplate).map((s) => s.printTemplate);
  }
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  public stateText: string;
  private notificationSubject: Subject<any> = new Subject();
  private notification$ = this.notificationSubject.asObservable();

  constructor(
    private coreDataService: DataService,
    private resourceService: ResourceManagerService,
    private bceService: BceService,
    private mapService: MapService,
    private abcCore: AbcCore,
    private cd: ChangeDetectorRef,
    private paraService: ParameterService,
    private abcContainer: AbcContainerService,
    private userService: UserService,
    private profileService: ProfileService,
    private messageBoxService: MessageBoxService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('abc-graphics-adapters-open-layers');
  }

  private async getPrintTemplatesFromProfile(): Promise<PrintTemplate[]> {
    this.isRequestingUpdateTemplates = true;
    this.updateStateText(this.res.get('Controls-Print-GetPrintTemplatesFromProfile'));
    const printTemplates: PrintTemplate[] = Array<PrintTemplate>();
    const generalProfileInfo: ProfileInfo = await this.getProfileInfo();

    // 'undefined' is the reactor for a called takeUntil :/
    if (generalProfileInfo !== undefined) {
      const profileInfo: ProfileInfoWithArenaViewModel = new ProfileInfoWithArenaViewModel(
        generalProfileInfo,
        this.abcCore.objectSpace,
      );
      if (profileInfo.settings) {
        const reportDocRefs: DefinitionDocumentReference[] =
          profileInfo.settings.findSpecialCommandDocuments('GisReport');
        if (reportDocRefs && reportDocRefs.length > 0) {
          const reportQueryPackages: {
            id: string;
            name: string;
            reportImageInfoQuery: Promise<ReportImageInfo[]>;
          }[] = [];
          for (const reportDocRef of reportDocRefs) {
            const reportQueryDef: ReportImageInfoQueryDef = new ReportImageInfoQueryDef();
            reportQueryDef.Id = reportDocRef.id;
            reportQueryPackages.push({
              id: reportDocRef.id,
              name: reportDocRef.name,
              reportImageInfoQuery: this.coreDataService
                .queryReportImageInfo(reportQueryDef)
                .pipe(takeUntil(this.unsubscribe$))
                .toPromise(),
            });
          }

          const errorReportNames: string[] = new Array<string>();
          for (const reportQueryPackage of reportQueryPackages) {
            try {
              const reps: ReportImageInfo[] = await reportQueryPackage.reportImageInfoQuery;
              // 'undefined' is the reactor for a called takeUntil :/
              if (reps !== undefined) {
                printTemplates.push(this.createPrintTemplate(reportQueryPackage.id, reportQueryPackage.name, reps));
              }
            } catch {
              errorReportNames.push(reportQueryPackage.name);
            }
          }
          if (errorReportNames.length > 0) {
            await this.showMessageBox(
              this.res.get('Controls-Print-ErrorWhileRefreshing') + '<br>' + errorReportNames.join('<br>'),
              MessageMode.Warning,
            );
          }
        }
      }
    }
    this.updateStateText(undefined);
    this.isRequestingUpdateTemplates = false;
    return printTemplates;
  }

  private async showMessageBox(text: string, mode: MessageMode) {
    const settings = new MessageSettings();
    settings.mode = mode;
    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = text;
    await this.messageBoxService.showMessageBox(settings).toPromise();
  }

  private createPrintTemplate(
    reportId: string,
    reportName: string,
    reportImageInfos: ReportImageInfo[],
  ): PrintTemplate {
    let newTemplate: PrintTemplate;
    const reportDetails = this.getReportDetails(reportImageInfos);
    if (reportDetails) {
      newTemplate = new PrintTemplate(
        reportId,
        reportName,
        reportDetails.imageParameterName,
        reportDetails.info,
        this.predefinedScales,
        reportDetails.dpiList,
        reportDetails.allowRotation,
      );
    }
    return newTemplate;
  }

  private async fillPrintTemplateSelectors(templates: PrintTemplate[]): Promise<void> {
    this.printTemplateSelectors = new Array<PrintTemplateSelector>();
    for (const template of templates) {
      this.printTemplateSelectors.push(new PrintTemplateSelector(null, null, template));
    }
    this.printTemplateSelectors.push(
      new PrintTemplateSelector(
        this.res.get('Controls-Print-RefreshTemplate'),
        () => this.updatePrintTemplates(),
        null,
      ),
    );

    const p = await this.getProfileInfo();
    const vm = new ProfileInfoViewModel(p);
    const result = vm.settings?.findCommandDocuments('Map_Print', 'Report');
    if (result?.allowCustomDocuments) {
      this.printTemplateSelectors.push(
        new PrintTemplateSelector(
          this.res.get('Controls-Print-ChooseTemplate'),
          () => this.selectReportForPrinting(),
          null,
        ),
      );
    }
  }

  async ngOnInit() {
    this.res = await this.resAsync;
    this.notification$.subscribe({
      next: (notification) => {
        const notifAttr: INotification = {
          id: 'error-notification',
          message: notification.message,
          type: notification.type,
          displayTime: notification.type === 'error' || notification.type === 'warning' ? 8000 : 4000,
        };
        this.abcContainer.createComponent(this.abcNotification, AbcNotificationComponent, notifAttr).componentRef
          .instance;
      },
    });
  }

  ngAfterViewInit() {
    this.subscriptionCancelled = this.bceService.selectDocumentCancelled.subscribe(() =>
      this.onSelectTemplateCancelled.bind(this)(),
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  private profileInfo: ProfileInfo;
  private async getProfileInfo(): Promise<ProfileInfo> {
    if (!this.profileInfo) {
      this.profileInfo = await this.profileService
        .getCurrentProfile(true)
        .pipe(takeUntil(this.unsubscribe$))
        .toPromise();
    }
    return this.profileInfo;
  }

  async initMap(mapView: MapView, mapLoader: MapLoader, profileId: string): Promise<void> {
    this.PRINT_SETTINGS_KEY = this.PRINT_SETTINGS_KEY + '_' + profileId;

    this.mapView = mapView;
    this.mapLoader = mapLoader;
    this.map = mapView.getMap();
    this.predefinedScales = mapLoader?.getScales();

    if (!this.predefinedScales || this.predefinedScales.length === 0) {
      this.predefinedScales = [DEFAULT_SCALE];
    }

    this.res = await this.resAsync;

    const templates: PrintTemplate[] = new Array<PrintTemplate>();
    const settings: PrintSettingsStorage = this.getPrintSettingsFromLocalStorage();
    if (!this.isSettingStructureOutdated(settings) && settings.templates && settings.templates.length > 0) {
      templates.push(...settings.templates);
    } else {
      try {
        this.disablePrintDialog(true);
        templates.push(...(await this.getPrintTemplatesFromProfile()));
      } catch (error) {
        this.abcCore.error.publish(error);
      } finally {
        this.disablePrintDialog(false);
      }
    }

    await this.ensureInitState(templates);
    this.saveTemplatesAndSelectionToLocalStorage();
  }

  private async ensureInitState(templates: PrintTemplate[]): Promise<void> {
    await this.initTemplates(templates);
    this.ensurePrintEnvironment();
    this.ensurePrintAreaFeature();
    this.zoomToPrintArea();
  }

  private getReportDetails(reportImageInfos: ReportImageInfo[]): {
    info: ReportImageInfo;
    imageParameterName: string;
    dpiList: number[];
    allowRotation: boolean;
  } {
    const reportItem = reportImageInfos.find((item) => item.ControlType === 'Report');

    const info = reportImageInfos.find(
      (info) =>
        info.ControlType === 'XRPictureBox' &&
        (info.Format?.startsWith('Image') || (!info.Format && info.OPath === '$Image')),
    );
    if (info) {
      let imageParameterName = 'Image';
      const start = info.OPath?.indexOf('$') + 1;
      let pos = start;
      while (
        pos &&
        pos < info.OPath.length &&
        ParameterHelper.isValidParameterName(info.OPath.substring(start, pos + 1))
      ) {
        pos++;
      }
      if (pos > start) {
        imageParameterName = info.OPath.substring(start, pos);
      }
      return {
        info: info,
        imageParameterName: imageParameterName,
        dpiList: reportItem?.Tag?.dpi ?? [],
        allowRotation: reportItem?.Tag?.rotatable,
      };
    }
  }

  private async initTemplates(templates: PrintTemplate[]): Promise<void> {
    await this.fillPrintTemplateSelectors(templates);

    this.selectDefaultTemplate();
    if (this.isRequestingUpdateTemplates) {
      this.disablePrintDialog(false);
      this.isRequestingUpdateTemplates = false;
    }
  }

  private selectDefaultTemplate() {
    const lastTemplateId: string = this.getTemplateSelectionFromLocalStorage();
    let template: PrintTemplateSelector;

    if (lastTemplateId) {
      template = this.printTemplateSelectors.find((s) => s?.printTemplate?.id === lastTemplateId);
    }
    if (!template) {
      template = this.printTemplateSelectors[0];
    }

    this.selectedSelector = template;
  }

  private ensurePrintAreaFeature() {
    this.refreshPrintAreaFeature();

    this.selectAction?.getFeatures().clear();
    if (this.printAreaFeature) {
      this.selectAction?.getFeatures().push(this.printAreaFeature);
    }
  }

  private refreshPrintAreaFeature() {
    let center: number[];
    if (this.printAreaFeature) {
      center = ol.extent.getCenter(this.printAreaFeature.getGeometry().getExtent());
    } else {
      center = this.mapView.getCurrentCenter();
    }
    if (center && this.mapView.dataProjection && this.mapView.featureProjection) {
      center = ol.proj.transform(center, this.mapView.featureProjection, this.mapView.dataProjection);
    }

    const size = this.getSelectedPrintSizeInMeters();
    let rect = this.calculateSelectedPrintPolygon(center, size);
    if (rect && this.mapView.dataProjection && this.mapView.featureProjection) {
      rect = rect.transform(this.mapView.dataProjection, this.mapView.featureProjection);
    }

    if (rect) {
      this.printAreaFeature = new ol.Feature({
        geometry: rect,
      });

      if (this.selectionLayerSource) {
        this.selectionLayerSource.clear();
        this.selectionLayerSource.addFeatures([this.printAreaFeature]);
      }
    }
  }

  private calculateSelectedPrintPolygon(center: number[], size: number[]) {
    let ret: any;

    if (this.mapView) {
      if (this.selectedSelector?.printTemplate) {
        if (size?.length > 1 && size[0] > 0 && size[1] > 0) {
          const h_w = size[0] / 2.0;
          const h_h = size[1] / 2.0;
          const x0 = center[0];
          const y0 = center[1];
          const p0 = [x0 - h_w, y0 - h_h];
          const p1 = [x0 + h_w, y0 - h_h];
          const p2 = [x0 + h_w, y0 + h_h];
          const p3 = [x0 - h_w, y0 + h_h];

          ret = new ol.geom.Polygon([[p0, p1, p2, p3, p0]]);
        }
      }
    }

    return ret;
  }

  private getSelectedPrintSizeInMeters(): number[] {
    return this.selectedSelector?.printTemplate?.getPrintSizeInMeters(this.selectedScale.value);
  }

  setVisible(visible: boolean): void {
    if (this.printDialogContainer && this.printDialogContainer.nativeElement)
      this.printDialogContainer.nativeElement.style.visibility = visible ? 'visible' : 'hidden';

    if (!visible) {
      this.clearPrintEnvironment();
    }
  }

  async startMarkingFeatures() {
    this.setPrintFeatureLayerVisibility(false);
    const entities = await this.mapService.selectDataEntities();
  }

  startPrinting() {
    this.updateStateText(undefined);
    const onAfterRestoreMap = (invalidDataUrl: boolean, hasLayerRenderError: boolean) => {
      if (invalidDataUrl && !hasLayerRenderError) {
        const origPixelRatio: number = this.map.pixelRatio_;
        this.map.pixelRatio_ = 1;
        this.startPrintingCore(false, (_) => (this.map.pixelRatio_ = origPixelRatio));
      }
    };

    this.startPrintingCore(true, onAfterRestoreMap);
  }

  private startPrintingCore(
    setInvalidDataUrlStateOnError: boolean,
    onAfterRestoreMap?: (hadInvalidDataUrl: boolean, hasLayerRenderError: boolean) => void,
  ): void {
    try {
      this.disablePrintDialog(true);
      //the following line causes the rendering!
      this.selectionLayer?.setVisible(false);
      const geom = this.printAreaFeature?.getGeometry();
      const extent = geom?.getExtent();
      const center: number[] = ol.extent.getCenter(extent);
      const sizeInMeters: number[] = this.getSelectedPrintSizeInMeters();

      const ring = geom.getCoordinates()[0];
      const p0 = ring[0];
      const p1 = ring[1];
      const rectRotation = Math.atan2(p1[1] - p0[1], p1[0] - p0[0]);
      const northRotation = -rectRotation;
      const olmapRotation = -northRotation;
      const oldRotation = this.mapView.getRotation();
      const oldRotationEnabled = this.mapView.getRotationEnabled();
      this.mapView.setRotationEnabled(true);
      this.mapView.setRotation(olmapRotation);

      let invalidDataUrl: boolean;
      let hasLayerRenderError: boolean;
      let onAfterRestoreMapWrapper = () => {
        if (onAfterRestoreMap) {
          onAfterRestoreMap(invalidDataUrl, hasLayerRenderError);
        }
        if (oldRotation !== undefined) {
          this.mapView.setRotation(oldRotation);
          if (!oldRotationEnabled) {
            this.mapView.setRotationEnabled(false);
          }
        }
      };

      this.mapView.printAsDataUrl(
        this.mapLoader,
        this.selectedScale.value,
        this.selectedResolution.dpi,
        center,
        sizeInMeters,
        (dataUrl, errorLoadingLayers: MapLayer[]) => {
          if (this.isValidDataUrl(dataUrl)) {
            const template = this.selectedSelector?.printTemplate;
            if (template) {
              const reportId = template.id;
              const exludeParameterList: string[] = [
                'Massstab',
                'Rotation',
                'Copyright',
                'Notes',
                template.opath ?? 'dummy',
              ];
              this.paraService
                .executeDocumentParametersIfExist(reportId, exludeParameterList)
                .subscribe((parameterValues) => {
                  this.downloadReportResult(dataUrl, northRotation, parameterValues);
                  this.closePrintDialog.emit();
                  this.clearPrintEnvironment();
                });
            } else {
              this.downloadReportResult(dataUrl, northRotation, undefined);
              this.closePrintDialog.emit();
              this.clearPrintEnvironment();
            }
          } else {
            hasLayerRenderError = errorLoadingLayers && errorLoadingLayers.length > 0;
            if (hasLayerRenderError) {
              const errorLayerText: string = this.res.get(
                'Controls-Print-ErrorWhileRenderingLayer',
                errorLoadingLayers[0].name,
              );
              this.updateStateText(errorLayerText);
              this.mapService.sendMapShortMessage(errorLayerText);
              this.hideProgress = true;
              this.selectionLayer?.setVisible(true);
              this.disabled = false;
            } else if (setInvalidDataUrlStateOnError) {
              invalidDataUrl = true;
            } else {
              this.updateStateText(this.res.get('Controls-Print-ErrorMessage'));
              this.mapService.sendMapShortMessage(this.res.get('Controls-Print-ErrorMessage'));
              this.hideProgress = true;
              this.selectionLayer?.setVisible(true);
              this.disabled = false;
            }
          }
        },
        onAfterRestoreMapWrapper,
        (s) => this.updateStateText(s),
      );
    } catch (e) {
      this.updateStateText(undefined);
      this.mapService.sendMapShortMessage(e.message);
      this.setVisible(false);
      this.hideProgress = true;
    }
  }

  private updateStateText(text: string): void {
    this.stateText = text;
    this.cd.detectChanges();
  }

  private isValidDataUrl(dataUrl: string): boolean {
    return dataUrl && dataUrl != 'data:,';
  }

  private disablePrintDialog(disable: boolean) {
    this.hideProgress = !disable;
    this.disabled = disable;
    this.cd.detectChanges();
  }
  private async downloadReportResult(dataUrl: string, rotation: number, parameters: ParameterValue[]) {
    const printTemplate: PrintTemplate = this.selectedSelector?.printTemplate;
    try {
      if (printTemplate) {
        this.updateStateText(this.res.get('Controls-Print-PreparingDownload'));
        const useClientPrinting: boolean = printTemplate.isBackColorTransparent;
        const imageAsBase64String = useClientPrinting ? '' : dataUrl.split(',')[1];
        const version = await this.userService.getServerVersion();
        const reportId = printTemplate.id;

        const paras = this.getFormPrintParameters(
          printTemplate,
          imageAsBase64String,
          version?.substring(0, 6) > '9.24.1',
          rotation,
          parameters,
        );
        const query = {
          Format: 'Pdf',
          Id: reportId,
          Parameters: paras,
        };
        const res: Blob = await this.coreDataService.exportDocumentResult(query, true).toPromise();
        this.setVisible(false);
        this.printDialogCloseDemand.emit();
        this.disablePrintDialog(false);

        const fileBlob = new Blob([res], {
          type: 'application/pdf',
          endings: 'transparent',
        });
        let downloadUrl = URL.createObjectURL(fileBlob);

        if (useClientPrinting) {
          const reportPdfBytes = await fetch(downloadUrl).then((res) => res.arrayBuffer());
          downloadUrl = await this.insertImageInPdf(reportPdfBytes, dataUrl, printTemplate);
        }

        const targetFileName: string = `map_${this.selectedResolution.displayText.replace(
          ' ',
          '_',
        )}_${new Date().getTime()}.pdf`;

        const isIpad: boolean =
          /Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
        const isAndroid: boolean = navigator.userAgent.includes('Android');

        if (isIpad || isAndroid) {
          const response = await fetch(downloadUrl);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onload = () => {
            const data = reader.result;
            const fileSystem = window['requestFileSystem'] || window['webkitRequestFileSystem'];
            fileSystem(
              window['PERSISTENT'],
              1024 * 1024,
              (fs) => {
                fs.root.getFile(
                  targetFileName,
                  { create: true, exclusive: false },
                  (fileEntry) => {
                    fileEntry.createWriter((fileWriter) => {
                      fileWriter.onwriteend = () => {};
                      fileWriter.write(new Blob([data], { type: 'application/pdf' }));
                    });
                  },
                  (error) => {
                    this.notificationSubject.next({
                      type: ToastType.Error,
                      message: 'Error occured',
                    });
                    console.error(error);
                  },
                );
              },
              (error) => console.error(error),
            );
          };
          reader.readAsArrayBuffer(blob);
          this.notificationSubject.next({
            type: ToastType.Success,
            message: this.res.get('Controls-Print-DownloadSaved'),
          });
        } else {
          const newWindow = this.abcCore.openWindow(downloadUrl);
          //is Popup-Blocker active: only then the direct download starts and has a nice filename :/
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            const doc = this.printDialogContainer.nativeElement.ownerDocument;
            const downloadLink = doc.createElement('a');
            downloadLink.setAttribute('download', targetFileName);
            downloadLink.setAttribute('href', downloadUrl);
            downloadLink.click();
          }
        }
      }
    } catch {
      this.setVisible(false);
      this.printDialogCloseDemand.emit();
      this.disablePrintDialog(false);
      await this.showMessageBox(
        this.res.get('Controls-Print-ErrorWhileDownloading', printTemplate.name, printTemplate.id),
        MessageMode.Error,
      );
    }
  }

  private async insertImageInPdf(
    reportPdfBytes: ArrayBuffer,
    imageDataUrl: string,
    printTemplate: PrintTemplate,
  ): Promise<string> {
    const reportPdfDoc = await PDFDocument.load(reportPdfBytes);
    const reportPdfPage = reportPdfDoc.getPages()[0]; // First page
    const { width, height } = reportPdfPage.getSize();

    // const flagUrl = 'http://localhost/Stuff/imgae_map.pdf';
    // const existingPdfBytes = await fetch(flagUrl).then((res) => res.arrayBuffer());
    // const imagePdfDoc = await PDFDocument.load(existingPdfBytes);

    let imagePdfDoc = await PDFDocument.create();
    const imagePdfPage = imagePdfDoc.addPage([width, height]);

    //https://github.com/Hopding/pdf-lib/issues/19#issuecomment-412275915
    const imagePdfWidth: number = printTemplate.picBoxWidthInInch * 72;
    const imagePdfHeight: number = printTemplate.picBoxHeightInInch * 72;
    const imagePdfLeft: number = printTemplate.picBoxLeftPosInInch * 72;
    const imagePdfBottom: number = printTemplate.picBoxBottomPosInInch * 72;

    //Absolut position for drawing picture in pdf-lib
    const imagePictureBoxPositionInPDF: PDFPageDrawImageOptions = {
      x: imagePdfLeft,
      y: imagePdfBottom,
      width: imagePdfWidth,
      height: imagePdfHeight,
    };
    const pdfImage = await imagePdfDoc.embedPng(imageDataUrl);
    imagePdfPage.drawImage(pdfImage, imagePictureBoxPositionInPDF);

    // const t: PDFPageDrawTextOptions = {
    //   x: 80,
    //   y: 120,
    //   size: 8,
    // };
    // let text = 'printTemplate.bandHeightInMM: ' + printTemplate.picBoxBandHeightInMM + '\n';
    // text += 'printTemplate.bottomMarginInMM: ' + printTemplate.pageBottomMarginInMM + '\n';
    // text += 'printTemplate.leftInMM: ' + printTemplate.picBoxLeftInMM + '\n';
    // text += 'printTemplate.leftMarginInMM: ' + printTemplate.pageLeftMarginInMM + '\n';
    // text += 'printTemplate.topInMM: ' + printTemplate.picBoxTopInMM + '\n';
    // imagePdfPage.drawText(text, t);

    // HACK: "Reload" the PDF, otherwise the drawn image isnt visible (bug)
    imagePdfDoc = await PDFDocument.load(await imagePdfDoc.save());

    const resultPdfDoc = await PDFDocument.create();
    resultPdfDoc.setProducer('');
    resultPdfDoc.setCreator('');

    // Embed the image and report PDF into new PDF
    const embeddedImagePage: PDFEmbeddedPage[] = await resultPdfDoc.embedPdf(imagePdfDoc);
    const embeddedReportPage: PDFEmbeddedPage[] = await resultPdfDoc.embedPdf(
      reportPdfDoc,
      reportPdfDoc.getPageIndices(),
    );

    // Add a blank page to the document
    let page = resultPdfDoc.addPage([width, height]);

    // Draw the image and report PDF into new PDF -> the order is important
    page.drawPage(embeddedImagePage[0]);
    page.drawPage(embeddedReportPage[0]);

    // Add other pages if exists in report template
    for (let i = 1; i < embeddedReportPage.length; i++) {
      const embeddedPage = embeddedReportPage[i];
      page = resultPdfDoc.addPage([embeddedPage.width, embeddedPage.height]);
      page.drawPage(embeddedReportPage[i]);
    }

    const resultPdfBytes: Uint8Array<ArrayBuffer> = (await resultPdfDoc.save()) as Uint8Array<ArrayBuffer>;
    return URL.createObjectURL(new Blob([resultPdfBytes], { type: 'application/pdf' }));
  }

  cancel() {
    this.setVisible(false);

    if (this.subscriptionCancelled) {
      this.subscriptionCancelled.unsubscribe();
    }
  }

  public onTemplateChange(): void {
    if (this.selectedSelector.action) {
      this.selectedSelector.action();
    } else {
      this.refreshPrintMask();
      this.saveTemplatesAndSelectionToLocalStorage();
    }
  }

  private saveTemplatesAndSelectionToLocalStorage(): void {
    const settings: PrintSettingsStorage = this.getPrintSettingsFromLocalStorage();
    settings.templates = this.printTemplates;
    settings.selectedId = this.selectedSelector?.printTemplate?.id;
    localStorage.setItem(this.PRINT_SETTINGS_KEY, JSON.stringify(settings));
  }

  private getTemplateSelectionFromLocalStorage(): string {
    const settings: PrintSettingsStorage = this.getPrintSettingsFromLocalStorage();
    return settings.selectedId;
  }

  private async updatePrintTemplates(): Promise<void> {
    try {
      this.disablePrintDialog(true);
      this.clearPrintEnvironment();
      await this.ensureInitState(await this.getPrintTemplatesFromProfile());
      this.saveTemplatesAndSelectionToLocalStorage();
    } catch (error) {
      this.abcCore.error.publish(error);
    } finally {
      this.disablePrintDialog(false);
    }
  }

  private selectReportForPrinting() {
    this.disablePrintDialog(true);

    this.bceService
      .selectDocument(GroupPacket.GROUP_ID_OPATHREPORTS, undefined, "//Column[contains(@format, 'Image')]")
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any) => {
          const reportId = data.Id;
          const reportName = data.Designation;
          const reportQueryDef: ReportImageInfoQueryDef = new ReportImageInfoQueryDef();
          reportQueryDef.Id = reportId;
          this.updateStateText(this.res.get('Controls-Print-LoadingTemplate', reportName));

          this.coreDataService.queryReportImageInfo(reportQueryDef).subscribe(
            (reportImageInfos) => {
              this.onReportInfoReturned(reportImageInfos, reportId, reportName);
              this.updateStateText(undefined);
            },
            (error) => {
              this.onPrintError(error.message);
            },
          );
        },
        (error) => {
          this.onPrintError(error.message);
        },
      );
  }

  private onSelectTemplateCancelled() {
    this.clearPrintEnvironment();

    setTimeout(() => {
      this.selectDefaultTemplate();
      this.refreshPrintMask();
      this.disablePrintDialog(false);
    }, 1000);
  }

  private onReportInfoReturned(reportImageInfos: ReportImageInfo[], reportId: string, reportName: string) {
    const msgInvalidTemplate = this.res.get('Controls-Print-InvalidTemplate');

    if (reportImageInfos && reportImageInfos.length > 0) {
      const reportDetails = this.getReportDetails(reportImageInfos);
      if (reportDetails) {
        let selector = this.printTemplateSelectors.find((t) => t.printTemplate?.id === reportId);
        if (!selector) {
          selector = new PrintTemplateSelector(
            null,
            null,
            new PrintTemplate(
              reportId,
              reportName,
              reportDetails.imageParameterName,
              reportDetails.info,
              this.predefinedScales,
              reportDetails.dpiList,
              reportDetails.allowRotation,
            ),
          );
          const firstActionSelector = this.printTemplateSelectors.findIndex((s) => s.action);
          if (firstActionSelector === -1) {
            this.printTemplateSelectors.push(selector);
          } else {
            this.printTemplateSelectors.splice(firstActionSelector, 0, selector);
          }
        }

        this.selectedSelector = selector;
        this.saveTemplatesAndSelectionToLocalStorage();

        this.cd.detectChanges();
        this.refreshPrintMask();
      } else {
        this.onPrintError(msgInvalidTemplate);
      }
    } else {
      this.onPrintError(msgInvalidTemplate);
    }

    this.disablePrintDialog(false);
  }

  private onPrintError(errorMsg: string) {
    this.clearPrintEnvironment();
    this.selectDefaultTemplate();
    this.refreshPrintMask();
    this.disablePrintDialog(false);
    this.updateStateText(errorMsg);
    this.mapService.sendMapShortMessage(errorMsg);
    this.cd.detectChanges();
  }

  private getPrintSettingsFromLocalStorage(): PrintSettingsStorage {
    let settings: PrintSettingsStorage = new PrintSettingsStorage();
    try {
      const existingSettings = localStorage.getItem(this.PRINT_SETTINGS_KEY);
      if (existingSettings) {
        settings = JSON.parse(existingSettings);
        for (const template of settings.templates) {
          Object.setPrototypeOf(template, PrintTemplate.prototype);
          for (const scale of template.allScales) {
            Object.setPrototypeOf(scale, PrintScale.prototype);
          }
          for (const resolution of template.allResolutions) {
            Object.setPrototypeOf(resolution, PrintResolution.prototype);
          }
        }
      }
    } catch {} //foo
    return settings;
  }

  private isSettingStructureOutdated(settings: PrintSettingsStorage): boolean {
    let outdated: boolean = false;
    //check if templates are outdated => new field allScales is missing
    if (settings.templates && settings.templates.length > 0) {
      if (!settings.templates[0].allScales || settings.templates[0].allScales.length === 0) {
        outdated = true;
      }
    }
    return outdated;
  }

  onScaleChange(scaleValue: number) {
    this.refreshPrintMask();
  }

  setPrintFeatureLayerVisibility(visible: boolean) {
    if (this.selectionLayer) {
      this.selectionLayer.setVisible(visible);
    }
  }

  private refreshPrintMask() {
    this.clearPrintEnvironment(false);
    this.ensurePrintEnvironment();
    this.ensurePrintAreaFeature();
    this.zoomToPrintArea();
  }

  private zoomToPrintArea() {
    if (this.mapView && this.printAreaFeature) {
      const geom = this.printAreaFeature.getGeometry();
      if (geom) {
        const ext = geom.getExtent();
        this.mapView.ensureExtentVisible(ext);
      }
    }
  }

  private ensurePrintEnvironment() {
    this.addSelectionLayer();
    this.addSelectionInteractions();
  }

  clearPrintEnvironment(deleteFeature: boolean = true) {
    if (deleteFeature) {
      this.selectAction?.getFeatures().clear();
      this.printAreaFeature = undefined;
    }

    this.clearInteractions();
    this.selectionLayerSource?.clear();
    this.map?.removeLayer(this.selectionLayerSource);

    this.selectionLayer = undefined;
    this.selectionLayerSource = undefined;
  }

  private addSelectionLayer() {
    this.selectionLayerSource = new ol.source.Vector();
    this.selectionLayer = this.createVectorLayer(this.selectionLayerSource);
    this.map.addLayer(this.selectionLayer);
  }

  private createVectorLayer(src: any): any {
    const vector = new ol.layer.Vector({
      name: LAYER_PRINT_BOX,
      source: src,
      style: this.createSelectionLayerBoxStyle(),
      zIndex: Infinity,
    });

    return vector;
  }

  private createSelectionLayerBoxStyle(): any {
    return new ol.style.Style({
      stroke: new ol.style.Stroke({
        width: 3,
        color: [255, 0, 0, 1, 0.8],
      }),
      fill: new ol.style.Fill({
        color: [0, 0, 255, 0.4],
      }),
    });
  }

  private addSelectionInteractions() {
    this.selectAction = new ol.interaction.Select({
      filter: function (feature, layer) {
        return layer.get('name') === LAYER_PRINT_BOX;
      },
      style: this.createSelectionLayerBoxStyle(),
    });

    this.translate = new ol.interaction.Translate({
      features: this.selectAction.getFeatures(),
    });

    var rotateHelper = new RotateInteraction(this.selectAction.getFeatures());
    this.rotateInteraction = rotateHelper.getInteraction();
    if (this.map) {
      this.map.addInteraction(this.selectAction);
      this.map.addInteraction(this.translate);
      this.updateRotateInteraction();
    }
  }

  private updateRotateInteraction(): void {
    if (this.map && this.rotateInteraction) {
      if (this.allowRotation) {
        this.map.addInteraction(this.rotateInteraction);
      } else {
        this.map.removeInteraction(this.rotateInteraction);
      }
    }
  }

  private clearInteractions() {
    if (this.map) {
      this.map.removeInteraction(this.selectAction);
      this.map.removeInteraction(this.translate);
      this.map.removeInteraction(this.rotateInteraction);
    }
  }

  private getFormPrintParameters(
    template: PrintTemplate,
    imageAsBase64String: string,
    getBase64BytesAsFileValue: boolean,
    rotation: number,
    additionalParameters: ParameterValue[],
  ): ParameterValue[] {
    const ret: ParameterValue[] = [
      {
        Name: 'Notes',
        Value: this.comment,
      },
      {
        Name: 'Massstab',
        Value: this.selectedScale.displayText,
      },
      {
        Name: 'Rotation',
        Value: rotation,
      },
      {
        Name: 'Copyright',
        Value: this.mapService.getVisibleAttributions
          ? this.mapService.getVisibleAttributions()?.reverse().join('\n')
          : '',
      },
      {
        Name: template.opath ?? 'dummy',
        Value: getBase64BytesAsFileValue
          ? ParameterHelper.getBase64BytesAsFileValue(imageAsBase64String)
          : imageAsBase64String,
      },
    ];

    if (additionalParameters?.length) {
      ret.push(...additionalParameters);
    }

    return ret;
  }
}
